// Confirm-quantity modal (T6). Renders for the pending inventory commit from
// useConfirmGate and echoes the exact qty / action / product / location (and
// the date when logging off-today) before the write lands. Closes a fat-finger
// gap: the Cmd+K palette and the cell popover both commit on Enter.
//
// Enter = confirm, Esc = cancel (Esc handled by the Modal shell via onClose).

import { useEffect } from 'react';
import { Modal } from './Modal.jsx';
import { displayName } from '../lib/products.ts';
import { locationLabel } from '../lib/locations.ts';

const ACTION_LABEL = { produced: '生产', received: '收货', sold: '售出', wasted: '报废', adjusted: '调整' };
const WASTE_REASONS = {
  expired: '过期', damaged: '损坏', overproduction: '多产', display: '展示',
  donated: '捐献', staff_taken: '店员取走', other: '其他'
};

// Build the echo line for a pending payload. `payload` is the gate payload:
//   { type:'log',      productId, kind, location, qty, reason? }
//   { type:'transfer', productId, from, to, qty }
function describe(payload, products, selectedDate, isToday) {
  const product = (products || []).find(p => p.id === payload.productId);
  const name = product ? displayName(product) : payload.productId;
  const dateTag = isToday ? '' : ` · ${selectedDate}`;
  if (payload.type === 'transfer') {
    return `确认 调拨 ${payload.qty} 件 ${name} ${locationLabel(payload.from)} → ${locationLabel(payload.to)}${dateTag}？`;
  }
  const action = ACTION_LABEL[payload.kind] || payload.kind;
  let reasonTag = '';
  if (payload.kind === 'wasted' && payload.reason) {
    reasonTag = ` · ${WASTE_REASONS[payload.reason] || payload.reason}`;
    // 店员取走 echoes WHICH staff so a misattribution is caught before commit.
    if (payload.reason === 'staff_taken' && payload.takenByName) {
      reasonTag += ` · ${payload.takenByName}`;
    }
    // 其他 echoes the note so the typed reason is confirmed before commit.
    if (payload.reason === 'other' && payload.note) {
      reasonTag += ` · ${payload.note}`;
    }
  }
  return `确认 ${action} ${payload.qty} 件 ${name} @ ${locationLabel(payload.location)}${reasonTag}${dateTag}？`;
}

export const ConfirmLogModal = ({ pending, products, selectedDate, isToday = true, onConfirm, onCancel }) => {
  const inFlight = !!pending?.inFlight;

  // Enter confirms (when not already committing).
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Enter') { e.preventDefault(); if (!inFlight) onConfirm(); }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onConfirm, inFlight]);

  if (!pending) return null;
  const message = describe(pending.payload, products, selectedDate, isToday);

  return (
    <Modal
      title="确认录入"
      onClose={() => { if (!inFlight) onCancel(); }}
      width={400}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onCancel} disabled={inFlight}>取消</button>
          <button className="btn btn-primary" onClick={onConfirm} disabled={inFlight} autoFocus>
            {inFlight ? '记录中…' : '确认'}
          </button>
        </>
      }>
      <div className="inv-confirm-msg">{message}</div>
    </Modal>
  );
};
